import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Grid, Button, TextField, ButtonGroup, Popper, Grow, Paper, ClickAwayListener, MenuList, MenuItem, ListItemIcon, ListItemText,
  FormControl, InputLabel, Select } from '@mui/material';
import { startOfMonth, subMonths } from 'date-fns';
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ArrowDropDownRounded, FileDownloadOutlined, SyncRounded } from '@mui/icons-material';
import { mainPaymentTypes, paymentTypes } from "utils/appConstants";

const defaultStartDate = startOfMonth(subMonths(new Date(), 1)), defaultEndDate = new Date();
const paymentCategories = [{name: 'All', value: 'All'}, ...paymentTypes, ...mainPaymentTypes];
const TaxReportFilters = ({loadData, startDate, endDate, downloadFile}) => {

  const defaultFilterValues = useMemo(() => ({from: startDate || defaultStartDate, to: endDate || defaultEndDate, paymentType: 'All', status: 'All'}), [startDate, endDate])
  const [filters, setFilters] = useState(defaultFilterValues);
  const [openMoreButtons, setOpenMoreButtons] = useState(false);
  const anchorRef = useRef(null);

  useEffect(() => {
    typeof loadData === 'function' && loadData(defaultFilterValues);
  }, [defaultFilterValues, loadData]);
  
  function onFetchBtnClick() {
    typeof loadData === 'function' && loadData(filters);
  }

  const handleClose = (event) => {
    if (anchorRef?.current && anchorRef?.current?.contains(event.target)) {
      return;
    }
    setOpenMoreButtons(false);
  };

  const handleToggle = () => {
    setOpenMoreButtons((prevOpen) => !prevOpen);
  };

  function onDownloadFileBtnClick(e) {
    handleToggle();
    typeof downloadFile === 'function' && setTimeout(downloadFile, 200);
  }
  
  return (
    <Grid container justifyContent="space-between" spacing={1}  >
      <Grid item container xs={12} spacing={2}  flexGrow={1}>
        <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
          <LocalizationProvider dateAdapter={AdapterDateFns} >
            <DatePicker
              required
              label="Start date"
              format="dd-MMM-yyyy"
              clearable={false}
              value={filters.from}
              maxDate={filters.to}
              onChange={(value) => setFilters(prevDate => ({...prevDate, from: value}))}
              openTo="day"
              views={['year', 'month', 'day']}
              textField={(params) => (
                <TextField 
                  value={filters.from}
                  {...params} 
                  margin="normal" 
                  sx={{width: '100%'}}
                />
              )}
              slotProps={{
                actionBar: {
                  actions: ['today'],
                },
                textField: {
                  fullWidth: true,
                  size: 'small'
                }
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={2} xl={2}>
          <LocalizationProvider dateAdapter={AdapterDateFns} >
            <DatePicker
              required
              label="End date"
              format="dd-MMM-yyyy"
              clearable={false}
              value={filters.to}
              minDate={filters.from}
              disableFuture
              onChange={(value) => setFilters(prevDate => ({...prevDate, to: value}))}
              openTo="day"
              views={['year', 'month', 'day']}
              textField={(params) => (
                <TextField 
                  value={filters.to}
                  {...params} 
                  margin="normal" 
                  sx={{width: '100%'}}
                />
              )}
              slotProps={{
                actionBar: {
                  actions: ['today'],
                },
                textField: {
                  fullWidth: true,
                  size: 'small'
                }
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={4} sm={4} md={2} lg={2} xl={2}>
          <FormControl fullWidth size='small'>
            <InputLabel id="status-label">Status</InputLabel>
            <Select   
              fullWidth
              name="status"                 
              labelId="status-label"
              id="status-select"
              label="Status"
              onChange={(event) => setFilters(prevDate => ({...prevDate, status: event.target.value}))}
              value={filters.status}
            >
              <MenuItem value='All'>All</MenuItem>
              <MenuItem value='Paid'>Paid</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={8} sm={6} md={4} lg={4} xl={3}  >
          <FormControl fullWidth  size='small'>
            <InputLabel id="paymentType-label">Payment Type</InputLabel>
            <Select   
              fullWidth
              value={filters.paymentType}
              name="paymentType"                 
              labelId="paymentType-label"
              id="paymentType-select"
              label="Payment Type"
              onChange={(event) => setFilters(prevDate => ({...prevDate, paymentType: event.target.value}))}
            >
              {paymentCategories.map(({value, name}) => <MenuItem key={value} value={name}>{name}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={12} lg={2} xl={3} textAlign="right" >
        <>
          <ButtonGroup variant="contained" ref={anchorRef} color="tertiary" sx={{position: 'relative'}}>
            <Button sx={{borderRadius: 1}} startIcon={<SyncRounded />} variant="tonal" onClick={onFetchBtnClick}>Refresh</Button>
            <Button
              size="small"
              aria-expanded={openMoreButtons ? 'true' : undefined}
              aria-haspopup="menu"
              onClick={handleToggle}
              sx={{borderTopRightRadius: '2px !important', borderBottomRightRadius: '2px !important'}}
            >
              <ArrowDropDownRounded  />
            </Button>
          </ButtonGroup>
          <Popper
            sx={{
              zIndex: 1,
            }}
            open={openMoreButtons}
            anchorEl={anchorRef.current}
            role={undefined}
            transition
            disablePortal
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin:
                    placement === 'bottom' ? 'center top' : 'center bottom',
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleClose}>
                    <MenuList id="split-button-menu" autoFocusItem>
                        <MenuItem onClick={onDownloadFileBtnClick}>
                          <ListItemIcon>
                            <FileDownloadOutlined  />
                          </ListItemIcon>
                          <ListItemText>Download Invoices</ListItemText>
                        </MenuItem>
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </>
        </Grid>
      </Grid>
    </Grid>
  );
}
 
export default  memo(TaxReportFilters);